const DEFAULT_PLAY_TIMEOUT_MS = 1500;
const TARGET_PLAY_DURATION_MS = 1000;
const DEFAULT_VOLUME = 1;
const MIN_PLAYBACK_RATE = 0.25;
const MAX_PLAYBACK_RATE = 4;

const audioCache = new Map();

function getAudioConstructor(audioFactory) {
  if (typeof audioFactory === 'function') return audioFactory;
  if (typeof window !== 'undefined' && typeof window.Audio === 'function') {
    return (src) => new window.Audio(src);
  }
  return null;
}

function getAudioElement(src, audioFactory) {
  const makeAudio = getAudioConstructor(audioFactory);
  if (!makeAudio || !src) return null;

  if (!audioCache.has(src)) {
    const audio = makeAudio(src);
    audio.preload = 'auto';
    audioCache.set(src, audio);
  }

  return audioCache.get(src);
}

export function clearNotificationSoundCache() {
  audioCache.forEach((audio) => {
    if (typeof audio.pause === 'function') {
      audio.pause();
    }
    audio.src = '';
  });
  audioCache.clear();
}

export function prepareNotificationSoundElement({
  src,
  enabled = true,
  volume = DEFAULT_VOLUME,
  audioFactory
}) {
  if (!enabled) return { prepared: false, reason: 'disabled' };

  const audio = getAudioElement(src, audioFactory);
  if (!audio) return { prepared: false, reason: 'unsupported' };

  audio.volume = volume;
  if (typeof audio.load === 'function') {
    audio.load();
  }

  return { prepared: true, reason: 'prepared' };
}

export function playNotificationSoundElement({
  src,
  enabled = true,
  volume = DEFAULT_VOLUME,
  timeoutMs = DEFAULT_PLAY_TIMEOUT_MS,
  audioFactory
}) {
  if (!enabled) {
    return Promise.resolve({ played: false, reason: 'disabled' });
  }

  const audio = getAudioElement(src, audioFactory);
  if (!audio || typeof audio.play !== 'function') {
    return Promise.resolve({ played: false, reason: 'unsupported' });
  }

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId;
    let playWindowId;

    const cleanup = () => {
      clearTimeout(timeoutId);
      clearTimeout(playWindowId);
      audio.loop = false;
      audio.playbackRate = 1;
      audio.removeEventListener?.('error', handleError);
    };

    const settle = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const handleError = () => settle({ played: false, reason: 'load_error' });
    audio.addEventListener?.('error', handleError, { once: true });

    timeoutId = setTimeout(() => {
      settle({ played: false, reason: 'timeout' });
    }, timeoutMs);

    try {
      if (typeof audio.pause === 'function') {
        audio.pause();
      }
      audio.currentTime = 0;
      audio.volume = volume;
      audio.loop = false;

      const sourceDurationSeconds = Number(audio.duration);
      if (Number.isFinite(sourceDurationSeconds) && sourceDurationSeconds > 0) {
        const targetDurationSeconds = TARGET_PLAY_DURATION_MS / 1000;
        const fittedRate = sourceDurationSeconds / targetDurationSeconds;
        audio.playbackRate = Math.min(MAX_PLAYBACK_RATE, Math.max(MIN_PLAYBACK_RATE, fittedRate));
      } else {
        audio.playbackRate = 1;
      }

      const playResult = audio.play();
      if (playResult && typeof playResult.then === 'function') {
        playResult
          .then(() => {
            playWindowId = setTimeout(() => {
              try {
                audio.pause?.();
                audio.currentTime = 0;
              } catch (_error) {
                // no-op
              }
              settle({ played: true, reason: 'played' });
            }, TARGET_PLAY_DURATION_MS);
          })
          .catch((error) => {
            const reason = error?.name === 'NotAllowedError' ? 'blocked' : 'load_error';
            settle({ played: false, reason });
          });
        return;
      }

      playWindowId = setTimeout(() => {
        try {
          audio.pause?.();
          audio.currentTime = 0;
        } catch (_error) {
          // no-op
        }
        settle({ played: true, reason: 'played' });
      }, TARGET_PLAY_DURATION_MS);
    } catch (error) {
      const reason = error?.name === 'NotAllowedError' ? 'blocked' : 'load_error';
      settle({ played: false, reason });
    }
  });
}
